import { useCallback, useEffect, useState } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { track } from '@/shared/analytics';

type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'theme-mode';

function getStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return 'light';
}

function applyMode(mode: ThemeMode) {
  /* eslint-disable i18next/no-literal-string -- CSS class name the dark-mode tokens hang off */
  if (mode === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  /* eslint-enable i18next/no-literal-string */
}

/**
 * Manages light/dark theme mode.
 *
 * Persists preference in localStorage and toggles the `dark` class
 * on `<html>` to activate monks-ui v4 dark mode tokens.
 *
 * Gated by the `dark_mode_toggle` feature flag via OpenFeature/Statsig.
 * When the flag is off, dark mode is disabled regardless of stored preference.
 */
export function useThemeMode() {
  const enabled = useBooleanFlagValue('dark_mode_toggle', false);
  const [mode, setMode] = useState<ThemeMode>(() => (enabled ? getStoredMode() : 'light'));

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line i18next/no-literal-string -- CSS class name the dark-mode tokens hang off
      document.documentElement.classList.remove('dark');
      return;
    }
    applyMode(mode);
  }, [mode, enabled]);

  const toggle = useCallback(() => {
    if (!enabled) return;
    // Compute next outside the updater so side effects (localStorage, analytics)
    // run once per click. React 18 StrictMode invokes state updaters twice in dev.
    const next: ThemeMode = mode === 'light' ? 'dark' : 'light';
    localStorage.setItem(STORAGE_KEY, next);
    track('theme_changed', { mode: next });
    setMode(next);
  }, [enabled, mode]);

  return { mode, toggle, isDark: enabled && mode === 'dark', enabled };
}
